import { Injectable } from '@angular/core';
import { BreakpointsEnum, OrientationEnum } from '@sd-portal/shared/models';
import { BehaviorSubject, fromEvent } from 'rxjs';
import { auditTime, distinctUntilChanged, map } from 'rxjs/operators';

import { assertUnreachable } from '../functions/assert-fns';

const throttleMs = 25;

@Injectable()
export class ScreenSizeService {
  private readonly screenSize = new BehaviorSubject<BreakpointsEnum>(
    this.breakpointForCurrentWindow()
  );
  private readonly screenOrientation = new BehaviorSubject<OrientationEnum>(
    this.orientationForCurrentWindow()
  );
  screenSize$ = this.screenSize.asObservable();
  screenOrientation$ = this.screenOrientation.asObservable();

  constructor() {
    // NOTE(jpr): inspired by https://stackoverflow.com/a/41518353/1273175Z
    fromEvent(window, 'resize')
      .pipe(
        auditTime(throttleMs),
        // NOTE(jpr): arrow syntax to keep `this` binding
        map(_ => this.breakpointForCurrentWindow()),
        distinctUntilChanged()
      )
      .subscribe(breakpoint => {
        this.screenSize.next(breakpoint);
      });

    fromEvent(window, 'resize')
      .pipe(
        auditTime(throttleMs),
        // NOTE(jpr): arrow syntax to keep `this` binding
        map(_ => this.orientationForCurrentWindow()),
        distinctUntilChanged()
      )
      .subscribe(orientation => {
        this.screenOrientation.next(orientation);
      });
  }

  // Helpers

  // calculate which breakpoint it's currently at
  private breakpointForCurrentWindow() {
    // NOTE(jpr): using this misdirection when defining so that the compiler catches if we add/remove
    // breakpoints without explicitly handling them
    const breakpoint = this.breakpointForWidth(window.outerWidth || window.innerWidth);
    switch (breakpoint) {
      case BreakpointsEnum.notavailable:
      case BreakpointsEnum.xs:
      case BreakpointsEnum.sm:
      case BreakpointsEnum.md:
      case BreakpointsEnum.lg:
      case BreakpointsEnum.xl:
      case BreakpointsEnum.xxl:
        return breakpoint;
    }

    // NOTE(jpr): if the compiler complains about this line then you should add the missing breakpoint
    // to `this.breakpointForWidth` and then handle it in the above switch
    return assertUnreachable(breakpoint);
  }

  private breakpointForWidth(windowWidth: number): BreakpointsEnum {
    if (typeof windowWidth !== 'number') {
      return BreakpointsEnum.notavailable;
    } else if (windowWidth < BreakpointsEnum.sm) {
      return BreakpointsEnum.xs;
    } else if (windowWidth >= BreakpointsEnum.sm && windowWidth < BreakpointsEnum.md) {
      return BreakpointsEnum.sm;
    } else if (windowWidth >= BreakpointsEnum.md && windowWidth < BreakpointsEnum.lg) {
      return BreakpointsEnum.md;
    } else if (windowWidth >= BreakpointsEnum.lg && windowWidth < BreakpointsEnum.xl) {
      return BreakpointsEnum.lg;
    } else if (windowWidth >= BreakpointsEnum.xl && windowWidth < BreakpointsEnum.xxl) {
      return BreakpointsEnum.xl;
    } else if (windowWidth >= BreakpointsEnum.xxl) {
      return BreakpointsEnum.xxl;
    } else {
      return BreakpointsEnum.notavailable;
    }
  }

  private orientationForCurrentWindow() {
    if (window.innerWidth > window.innerHeight) {
      return OrientationEnum.landscape;
    } else {
      return OrientationEnum.portrait;
    }
  }
}
